import React, { FC, useRef, useState } from 'react';

import { Box } from '@rbilabs/component-library';
import { isNil } from 'lodash-es';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { IStore, IStoreHoursOfOperation } from '@rbi-ctg/store';
import ActionButton from 'components/action-button';
import IconCatering from 'components/icons/catering';
import IconCurbside from 'components/icons/curbside';
import IconDelivery from 'components/icons/delivery';
import IconDriveThru from 'components/icons/drive-thru';
import IconPhone from 'components/icons/phone';
import Modal from 'components/modal';
import { Content, HeroContainer } from 'components/modal/css';
import { ImParticleEventData } from 'components/modal/types';
import Picture from 'components/picture';
import StaticMap from 'components/static-map';
import DirectionsLink from 'components/store-card/directions-link';
import { useGetRestaurantQuery } from 'generated/rbi-graphql';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { getRestaurant, getRestaurantsByStoreNumber } from 'remote/api/restaurants';
import { useGeolocation } from 'state/geolocation';
import { useFlag } from 'state/launchdarkly';
import { useNetworkContext } from 'state/network';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import {
  getUSState,
  isMobileOrderingAvailable,
  mergeRestaurantData,
  readableTimeInterval,
  useGetRestaurantAvailabilityFn,
} from 'utils/restaurant';
import { routes } from 'utils/routing';
import { isCatering } from 'utils/service-mode';
import { getStoreSEOSlug } from 'utils/slugify';

import StoreMeta from './meta';
import { messages } from './store-info-modal-messages';
import theme from './theme';

const Background = styled.div`
  background: ${theme.modalBackground};
  width: 100%;
`;

const Inner = styled.div`
  max-width: 485px;
  width: 90%;
  margin: 0 auto;
  background: ${theme.modalBackground};
  color: ${Styles.color.grey.one};
  padding-top: 20px;

  > * {
    width: 100%;
    display: block;
  }

  p {
    margin: 0 0 1rem 0;
    color: ${Styles.color.grey.one};
    line-height: 1.4;
    font-size: 0.9375rem;
    text-transform: capitalize;
  }

  h2,
  h3 {
    color: ${Styles.color.black};
    font-family: ${Styles.fontFamily.base};
    margin: 0;
    text-transform: capitalize;
  }

  h3 {
    margin-bottom: 14px;
  }

  a {
    color: ${Styles.color.grey.one};
    font-weight: 500;
    font-size: 0.875rem;
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Address = styled.div`
  font-size: 0.9375rem;
  color: ${Styles.color.grey.one};
  text-transform: capitalize;
  line-height: 1.4;
`;

const PhoneNumber = styled.a`
  color: ${Styles.color.grey.one};
  line-height: 1.4;
  font-size: 0.9375rem;
`;

const Amenity = styled.div`
  margin-bottom: 14px;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  > svg {
    margin-right: 5px;
    width: 30px;
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const SpacingLine = styled.div<{ bottom: number; top: number }>`
  border-top: 1px solid rgba(177, 177, 177, 0.7);
  margin-top: ${p => p.top}px;
  margin-bottom: ${p => p.bottom}px;
`;

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 0;
  border-top: 1px solid rgba(177, 177, 177, 0.7);
  width: 100%;
  height: 81px;
  display: flex;
  align-items: center;
  background: ${theme.modalBackground};

  ${Styles.mobileFullscreen`
    /* using the full safe-area inset makes this component too tall */
    padding-bottom: calc(env(safe-area-inset-bottom) / 2);
  `}
`;

const StoreHourContainer = styled.div`
  margin: 1rem 0;
  display: flex;
  justify-content: space-between;
`;

const AddressAndPhone = styled.div`
  * {
    font-size: 1rem !important;
    line-height: 1.5;
  }

  a:first-of-type {
    font-weight: bold;
  }
`;

const StoreModal = styled(Modal)`
  ${Styles.desktop`
    &&& {
      width: ${Styles.layout.smallerSectionMaxWidth};
    }
  `};
`;

interface IHoursProps {
  hours: IStoreHoursOfOperation;
  title: string;
}

const Hours = ({ title, hours }: IHoursProps) => {
  const { formatMessage } = useIntl();
  const hoursOfOperation = readableTimeInterval(
    formatMessage({ id: 'hoursOfOperationFallbackMessage' }),
    formatMessage({ id: 'open24Hours' })
  );

  const storeHoursSummary: [string, string][] = [
    ['monday', 'mon'],
    ['tuesday', 'tue'],
    ['wednesday', 'wed'],
    ['thursday', 'thr'],
    ['friday', 'fri'],
    ['saturday', 'sat'],
    ['sunday', 'sun'],
  ];

  if (!hours) {
    return null;
  }

  return (
    <div>
      <h3>{title}</h3>
      {storeHoursSummary.map(([messageId, abbreviatedDay]) => {
        const openingHours = hours[`${abbreviatedDay}Open`];
        const closingHours = hours[`${abbreviatedDay}Close`];
        return (
          <StoreHourContainer key={messageId}>
            <div>{formatMessage(messages[messageId])}</div>
            <div>{hoursOfOperation(openingHours, closingHours)}</div>
          </StoreHourContainer>
        );
      })}
    </div>
  );
};

interface IStoreInfoModalProps {
  disableCanonical?: boolean;
  isRestaurantPosOnline: boolean;
  onDismiss?: () => void;
  onOpen?: () => void;
  mParticleEventData: ImParticleEventData;
  showOrderCTA?: boolean;
  store: IStore;
}

export const StoreInfoModal = ({
  disableCanonical = false,
  isRestaurantPosOnline,
  mParticleEventData,
  onDismiss,
  onOpen,
  showOrderCTA = true,
  store,
}: IStoreInfoModalProps) => {
  const {
    name: storeName,
    phoneNumber,
    restaurantImage,
    latitude: lat,
    longitude: lng,
    physicalAddress,
  } = store;
  const { current: position } = useRef({ lat, lng });
  const { selectStore } = useOrderContext();
  const { activeCoordinates } = useGeolocation();
  const { serviceMode } = useServiceModeContext();
  const { pathname }: { pathname: string } = useLocation();
  const { formatMessage } = useIntl();
  const { serviceModeStatus, allServiceModesUnavailable } = useServiceModeStatus(store);
  const enableOrderingFlag = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const enableOrdering = isNil(enableOrderingFlag) || enableOrderingFlag;
  const navigate = useNavigate();

  const handleRestaurantSelect = () => {
    selectStore(store, () => {
      isCatering(serviceMode)
        ? navigate(routes.menu)
        : navigate(routes.pickupMode, { replace: true });
    });
  };

  useEffectOnce(() => onOpen?.());

  const streetAddress = physicalAddress?.address1;
  const formattedCity = physicalAddress?.city;
  const state = getUSState(physicalAddress?.stateProvince);
  const postalCode = physicalAddress?.postalCode.split('-')[0];
  const shouldShowWaysToOrderSection =
    isMobileOrderingAvailable(store) || !allServiceModesUnavailable;

  const canonicalUrl = [`${routes.store}`, getStoreSEOSlug(store)].join('/');
  const isStoreLocatorPage = pathname.includes(routes.store);

  const shouldShowDineIn = serviceModeStatus.EAT_IN.capable && !serviceModeStatus.EAT_IN.disabled;
  const shouldShowTakeout =
    serviceModeStatus.TAKEOUT.capable && !serviceModeStatus.TAKEOUT.disabled;
  const shouldShowDriveThru =
    serviceModeStatus.DRIVE_THRU.capable && !serviceModeStatus.DRIVE_THRU.disabled;
  const shouldShowCurbside =
    serviceModeStatus.CURBSIDE.capable && !serviceModeStatus.CURBSIDE.disabled;

  const hasCatering =
    serviceModeStatus.CATERING_DELIVERY.available || serviceModeStatus.CATERING_PICKUP.available;

  const amenitiesBreakdown: [FC<{ height: string; fill: string }>, boolean, string, string][] = [
    [IconDriveThru, serviceModeStatus.DRIVE_THRU.available, '18px', 'driveThru'],
    [IconPhone, isMobileOrderingAvailable(store), '22px', 'mobileOrdering'],
    [IconDelivery, serviceModeStatus.DELIVERY.available, '20px', 'delivery'],
    [IconCurbside, serviceModeStatus.CURBSIDE.available, '22px', 'curbside'],
    [IconCatering, hasCatering, '20px', 'catering'],
  ];

  const storeHoursBreakdown = [
    [shouldShowDineIn || shouldShowTakeout, 'dineInHours', 'diningRoomHours'],
    [shouldShowDriveThru, 'driveThruHours'],
    [shouldShowCurbside, 'curbsideHours'],
  ];

  const isOrderingDisabled =
    !isMobileOrderingAvailable(store) || allServiceModesUnavailable || !isRestaurantPosOnline;

  return (
    <>
      {!disableCanonical && (
        <Helmet data-testid="canonical" link={[{ rel: 'canonical', href: canonicalUrl }]} />
      )}
      <StoreModal
        onDismiss={onDismiss}
        data-testid="store-info-modal"
        mParticleEventData={mParticleEventData}
      >
        {isStoreLocatorPage && (
          <StoreMeta
            storeUrl={window.location.href}
            atCopy={formatMessage(messages.at)}
            streetAddress={streetAddress}
            city={formattedCity}
            state={state}
            postalCode={postalCode}
            phoneNumber={phoneNumber}
            image={restaurantImage}
          />
        )}
        <Container>
          <HeroContainer>
            {restaurantImage ? (
              <Picture image={restaurantImage} alt={storeName} />
            ) : (
              <StaticMap position={position} />
            )}
          </HeroContainer>
          <Content disableFooter={!enableOrdering}>
            <Background>
              <Inner>
                <h2 id="modal-heading">{streetAddress.toLowerCase()}</h2>
                <AddressAndPhone>
                  <DirectionsLink activeCoordinates={activeCoordinates} position={position} />
                  <Address>{`${formattedCity} ${state}, ${postalCode}`}</Address>
                  {phoneNumber && (
                    <PhoneNumber href={`tel:${phoneNumber}`}>{phoneNumber}</PhoneNumber>
                  )}
                </AddressAndPhone>
                <SpacingLine top={24} bottom={11} />
                {enableOrdering && (
                  <>
                    {shouldShowWaysToOrderSection && <h3>{formatMessage(messages.waysToOrder)}</h3>}
                    {amenitiesBreakdown.map(
                      ([Icon, shouldShow, height, messageId]) =>
                        shouldShow && (
                          <Amenity key={messageId}>
                            <Icon height={height} fill={Styles.color.grey.one} />
                            {formatMessage(messages[messageId])}
                          </Amenity>
                        )
                    )}
                    {shouldShowWaysToOrderSection && <SpacingLine top={4} bottom={15} />}
                  </>
                )}
                {storeHoursBreakdown.map(
                  ([shouldShow, messageId, hours = messageId]: [boolean, string, string]) =>
                    shouldShow && (
                      <Hours
                        key={messageId}
                        title={formatMessage(messages[messageId])}
                        hours={store[hours]}
                      />
                    )
                )}
              </Inner>
            </Background>
          </Content>
          {showOrderCTA && enableOrdering && (
            <ButtonContainer>
              <Box maxWidth="300px" margin="0 auto">
                <ActionButton
                  fullWidth
                  disabled={isOrderingDisabled}
                  onClick={handleRestaurantSelect}
                >
                  {formatMessage({ id: 'orderHere' })}
                </ActionButton>
              </Box>
            </ButtonContainer>
          )}
        </Container>
      </StoreModal>
    </>
  );
};

interface IStoreInfoModalContainerProps {
  storeId?: string;
  disableCanonical?: boolean;
  canonicalUrl?: string;
  storeNumber?: string;
  onDismiss: () => void;
  onOpen?: () => void;
  showOrderCTA?: boolean;
}

const StoreInfoModalContainer = (props: IStoreInfoModalContainerProps) => {
  const { storeNumber: storeNumberFromProps, storeId: storeIdFromProps } = props;
  const { id: storeIdFromUrl, storeNumber: storeNumberFromUrl } = useParams();
  const { connection, setHasNetworkError, sanityEndpoints } = useNetworkContext();
  const [store, setStore] = useState<IStore | undefined>();
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Fetching Restaurant Info Failure',
  });

  const storeId = storeIdFromProps || storeIdFromUrl;
  const storeNumber = storeNumberFromProps || storeNumberFromUrl;

  const checkAvailability = useGetRestaurantAvailabilityFn();

  const { data } = useGetRestaurantQuery({
    fetchPolicy: 'cache-and-network',
    skip: !store?.number,
    variables: { storeId: store?.number },
  });

  const rbiRestaurant = data?.restaurant;

  const parsedStore =
    store && rbiRestaurant && mergeRestaurantData({ rbiRestaurant, sanityStore: store });

  const getStoreInfo = async () => {
    if (!storeId && !storeNumber) {
      return;
    }

    let sanityStore: IStore | undefined;

    try {
      if (storeId) {
        [sanityStore] = await getRestaurant(sanityEndpoints.groq, {
          connection,
          id: storeId,
        });
      } else if (storeNumber) {
        [sanityStore] = await getRestaurantsByStoreNumber(sanityEndpoints.groq, {
          connection,
          storeNumber,
        });
      }

      setStore(sanityStore);
    } catch (err) {
      if (err.connectionError) {
        setHasNetworkError(true);
        return;
      }
      openErrorDialog({ message: 'Error fetching restaurant info', error: err });
    }
  };

  useEffectOnce(() => {
    getStoreInfo();
  });

  const modalProps = {
    ...props,
    isRestaurantPosOnline: checkAvailability(rbiRestaurant),
    mParticleEventData: { modalAppearanceEventMessage: 'Store Details' },
    store: parsedStore,
  } as IStoreInfoModalProps;

  return (
    <>
      {parsedStore && (
        <div>
          <StoreInfoModal {...modalProps} />
          <ErrorDialog />
        </div>
      )}
    </>
  );
};

export default StoreInfoModalContainer;
